import React, { useEffect, useState } from 'react';
import {
  Box,
  PropTypes,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import useStyles from './styles';

import { View } from 'components';
import { ReportType, ReportViewType } from 'types';
import ErrorComponent from 'components/error/error-component';
import { descendingComparatorByArray, getNestedProperty } from 'helpers/common.helper';
import { ReportViewReportStructure, getHeaderTable } from 'helpers/report-helpers/constants';
import { formatValue, generateReportViewRowData } from 'helpers/report-helpers/utils';
import { DataType } from 'helpers/report-helpers';

const getTableCellAlignment = (type): string => {
  return type === 'number' || type === 'numberYtd' ? 'right' : 'left';
};

interface Props {
  reportType: ReportType;
  data: Array<any>;
  loading: boolean;
  statusCode?: number;
  defaultSort?: string;
  defaultSortByValue?: string[];
  defaultOrder?: string | number | symbol;
  setUserSortBy?: ({
    key,
    order,
    keys,
  }: {
    key: string;
    order: 'asc' | 'desc';
    keys: string[];
  }) => void;
  setUserSortByValue?: (any) => void;
  validBankReconciliationBusiness?: boolean;
  reportView?: ReportViewType;
}

export default function ReportTable({
  reportType,
  data,
  loading,
  statusCode,
  defaultSort,
  defaultSortByValue,
  defaultOrder,
  setUserSortBy,
  setUserSortByValue,
  validBankReconciliationBusiness = false,
  reportView,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  const [headerTable, setHeaderTable] = useState(getHeaderTable(reportType));
  const [order, setOrder] = useState<any>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortByValue, setSortByValue] = useState<string[]>([]);
  const [reportData, setReportData] = useState(data || []);
  const [reportViewData, setReportViewData] = useState(null);

  useEffect(() => {
    setSortBy(defaultSort);
    setOrder(defaultOrder);
    setSortByValue(defaultSortByValue);
  }, [defaultSort, defaultOrder, defaultSortByValue]);

  useEffect(() => {
    // we only really want to update the header when the data changes.
    if (reportView !== ReportViewType.REPORT) setHeaderTable(getHeaderTable(reportType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect((): void => {
    let sorted = data;
    if (sortByValue?.length) {
      sorted = data.sort((a, b) =>
        order === 'desc'
          ? descendingComparatorByArray(a, b, sortByValue)
          : -descendingComparatorByArray(a, b, sortByValue),
      );
    }
    if (reportView === ReportViewType.REPORT) {
      const generatedData = generateReportViewRowData(
        sorted,
        ReportViewReportStructure(reportType),
      );
      setReportViewData(generatedData);
    }
    setReportData(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, sortByValue, data]);

  const handleRequestSort = (key: string, keys: string[]) => (): void => {
    const isAsc = sortBy === key && order === 'asc';
    const sortOrder = isAsc ? 'desc' : 'asc';
    setOrder(sortOrder);
    setSortBy(key);
    setSortByValue(keys);
    setUserSortBy({ key, order: sortOrder, keys });
    setUserSortByValue(keys);
  };

  if (statusCode && !reportData.length) {
    const showBankReconciliation404Error =
      !validBankReconciliationBusiness &&
      reportType === ReportType.BANKING_RECONCILIATION &&
      statusCode === 404;

    return (
      <View flexGrow alignItems="center" justifyContent="flex-start">
        <Box className={classes.container}>
          <ErrorComponent
            statusCode={statusCode}
            subTitle={
              showBankReconciliation404Error
                ? 'DASHBOARD_BUSINESS_BANK_RECONCILIATION_SERVICE_CRITERIA_MISSING'
                : ''
            }
          />
        </Box>
      </View>
    );
  }

  if ((!loading && !reportData.length) || (loading && !reportData.length)) {
    return null;
  }

  return (
    <View id="report-table" flexGrow className={classes.table}>
      {reportView === ReportViewType.REPORT && reportViewData?.rowData ? (
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell tabIndex={0} style={{ zIndex: 3 }}></TableCell>
              {reportViewData.periods.map((period) => (
                <TableCell key={period} tabIndex={0}>
                  {period}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ReportViewReportStructure(reportType).map(({ name, key, type, style }, index) => {
              if (type === DataType.TOP_LEVEL_KEY) {
                return (
                  <>
                    <TableRow key={`${key || ''}-${index}`} role="tableRow" style={style}>
                      <TableCell
                        tabIndex={0}
                        className={`${classes.topLevelReportCell} ${classes.stickyColumn}`}
                        style={style}
                      >
                        {name}
                      </TableCell>
                      <TableCell style={style} colSpan={reportData.length}></TableCell>
                    </TableRow>
                  </>
                );
              } else if (type === DataType.NESTED_OBJECT) {
                return (
                  <>
                    <TableRow key={`${key || ''}-nested-${index}`} role="tableRow" style={style}>
                      <TableCell
                        tabIndex={0}
                        className={`${classes.categoryReportCell} ${classes.stickyColumn}`}
                        style={style}
                      >
                        {name}
                      </TableCell>
                      <TableCell style={style} colSpan={reportData.length}></TableCell>
                    </TableRow>
                    {Object.entries(reportViewData.rowData[key]).map(([subcat, values]) => {
                      return (
                        <>
                          <TableRow key={`${key || ''}-subcat-${index}`} role="tableRow">
                            <TableCell
                              tabIndex={0}
                              className={`${classes.subcategoryReportCell} ${classes.stickyColumn}`}
                            >
                              {subcat}
                            </TableCell>
                            {reportViewData.periods.map((period) => (
                              <TableCell tabIndex={0}>
                                {/* to do: change symbol as per currency */}
                                {values[period] || '$0'}
                              </TableCell>
                            ))}
                          </TableRow>
                        </>
                      );
                    })}
                  </>
                );
              } else {
                return (
                  <TableRow key={`${key || ''}-${index}`} role="tableRow">
                    <TableCell
                      tabIndex={0}
                      className={`${classes.categoryReportCell} ${classes.stickyColumn}`}
                      style={style}
                    >
                      {name}
                    </TableCell>
                    {reportViewData.periods.map((period) => (
                      <TableCell tabIndex={0} style={style}>
                        {reportViewData.rowData[key][period] || '$0'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              }
            })}
          </TableBody>
        </Table>
      ) : (
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headerTable.map(({ name, type, key, keys, sort }) => (
                <TableCell
                  key={key}
                  tabIndex={0}
                  sortDirection={sort !== false && sortBy === key ? order : false}
                  align={getTableCellAlignment(type) as PropTypes.Alignment}
                >
                  {sort !== false ? (
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? order : 'asc'}
                      onClick={handleRequestSort(key, keys)}
                    >
                      {t(name)}
                      {sortBy === key ? (
                        <span className={classes.sortIcon}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    t(name)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData?.map((item, id) => (
              <TableRow key={`${item?.createdAt || ''}${id}`} role="tableRow">
                {headerTable.map(({ key, keys, type, style, decimals = 2 }: any, index) => (
                  <TableCell
                    tabIndex={0}
                    key={`${item?.createdAt}${key}${index}`}
                    align={getTableCellAlignment(type) as PropTypes.Alignment}
                    style={style}
                  >
                    {formatValue(getNestedProperty(item, keys), type, decimals, key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </View>
  );
}
